import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import Swal from "sweetalert2";
import PartnerFooter from "../../component/PartnerFooter";
import PartnerHeader from "../../component/PartnerHeader";

const API_BASE = "/api";
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];

/* ---------------------------------------------------------------
   Empty records
---------------------------------------------------------------- */

const emptyPartner = {
  id: "",
  name: "",
  designation: "",
  companyName: "",
  companyType: "",
  email: "",
  mobile: "",
  website: "",
  gst: "",
  pan: "",
  state: "",
  city: "",
  address: "",
  pincode: "",
  remark: "",
  status: "",
  createdAt: "",
  createdDate: "",
};

const emptyCompany = {
  id: "",
  partnerId: "",
  companyLegalName: "",
  companyDisplayName: "",
  companyDescription: "",
  industry: "",
  companySize: "",
  yearEstablished: "",
  officialEmail: "",
  officialPhone: "",
  alternatePhone: "",
  officialWebsite: "",
  registeredAddress: "",
  officeAddress: "",
  state: "",
  city: "",
  pincode: "",
  gstNumber: "",
  panNumber: "",
  linkedinUrl: "",
  facebookUrl: "",
  instagramUrl: "",
  twitterUrl: "",
  aboutCompany: "",
  remark: "",
  status: "",
  logoFileName: "",
  gstCertificateFileName: "",
  panCardFileName: "",
  companyRegistrationFileName: "",
};

const blankCompany = (partnerId = "") => ({ ...emptyCompany, partnerId });

/* ---------------------------------------------------------------
   Configuration
---------------------------------------------------------------- */

const DOCUMENTS = {
  logo: {
    short: "LOGO",
    title: "Company Logo",
    description: "Upload your official company logo (PNG, JPG, JPEG or WEBP).",
    endpoint: (partnerId) =>
      `${API_BASE}/company-details/partner/${partnerId}/logo`,
    viewEndpoint: (partnerId) =>
      `${API_BASE}/company-details/partner/${partnerId}/logo`,
    accept: "image/png,image/jpeg,image/jpg,image/webp",
    fileKey: "logoFileName",
  },
  gst: {
    short: "GST",
    title: "GST Certificate",
    description: "Upload your valid GST registration certificate.",
    endpoint: (partnerId) =>
      `${API_BASE}/company-details/partner/${partnerId}/gst`,
    viewEndpoint: (partnerId) =>
      `${API_BASE}/company-details/partner/${partnerId}/gst`,
    accept: ".pdf,image/png,image/jpeg,image/jpg,.webp",
    fileKey: "gstCertificateFileName",
  },
  pan: {
    short: "PAN",
    title: "PAN Card",
    description: "Upload company PAN card.",
    endpoint: (partnerId) =>
      `${API_BASE}/company-details/partner/${partnerId}/pan`,
    viewEndpoint: (partnerId) =>
      `${API_BASE}/company-details/partner/${partnerId}/pan`,
    accept: ".pdf,image/png,image/jpeg,image/jpg,.webp",
    fileKey: "panCardFileName",
  },
  registration: {
    short: "REG",
    title: "Company Registration",
    description: "Upload company registration document.",
    endpoint: (partnerId) =>
      `${API_BASE}/company-details/partner/${partnerId}/registration-document`,
    viewEndpoint: (partnerId) =>
      `${API_BASE}/company-details/partner/${partnerId}/registration-document`,
    accept: ".pdf,image/png,image/jpeg,image/jpg,.webp",
    fileKey: "companyRegistrationFileName",
  },
};

const DOCUMENT_FILE_KEYS = Object.values(DOCUMENTS).map((d) => d.fileKey);

// Fields sent to the backend
const PARTNER_PAYLOAD_KEYS = [
  "name", "designation", "companyName", "companyType", "email", "mobile",
  "website", "gst", "pan", "state", "city", "address", "pincode", "remark",
];

const COMPANY_PAYLOAD_KEYS = [
  "companyLegalName", "companyDisplayName", "companyDescription", "industry",
  "companySize", "yearEstablished", "officialEmail", "officialPhone",
  "alternatePhone", "officialWebsite", "registeredAddress", "officeAddress",
  "state", "city", "pincode", "gstNumber", "panNumber", "linkedinUrl",
  "facebookUrl", "instagramUrl", "twitterUrl", "aboutCompany", "remark",
];

// Form layouts (locked = never editable, full = spans the whole row)
const PARTNER_INFO_FIELDS = [
  { key: "name", label: "Full Name" },
  { key: "designation", label: "Designation" },
  { key: "companyName", label: "Company Name" },
  { key: "companyType", label: "Company Type" },
  { key: "email", label: "Email", locked: true },
  { key: "mobile", label: "Mobile Number" },
  { key: "website", label: "Website" },
  { key: "status", label: "Status", locked: true },
];

const PARTNER_ADDRESS_FIELDS = [
  { key: "address", label: "Address", full: true },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "pincode", label: "Pincode" },
];

const COMPANY_FIELDS = [
  { key: "companyLegalName", label: "Legal Name" },
  { key: "companyDisplayName", label: "Display Name" },
  { key: "industry", label: "Industry" },
  { key: "companySize", label: "Company Size" },
  { key: "yearEstablished", label: "Year Established" },
  { key: "officialEmail", label: "Official Email" },
  { key: "officialPhone", label: "Official Phone" },
  { key: "alternatePhone", label: "Alternate Phone" },
  { key: "officialWebsite", label: "Website" },
  { key: "gstNumber", label: "GST Number" },
  { key: "panNumber", label: "PAN Number" },
];

const COMPANY_ADDRESS_FIELDS = [
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "pincode", label: "Pincode" },
];

const COMPANY_ADDRESS_TEXTAREAS = [
  { key: "registeredAddress", label: "Registered Address" },
  { key: "officeAddress", label: "Office Address" },
];

const COMPANY_SOCIAL_FIELDS = [
  { key: "linkedinUrl", label: "LinkedIn URL" },
  { key: "facebookUrl", label: "Facebook URL" },
  { key: "instagramUrl", label: "Instagram URL" },
  { key: "twitterUrl", label: "Twitter URL" },
];

// Maps every editable "section" of the page to what the modal needs to render.
const MODAL_CONFIG = {
  partnerInfo: {
    title: "Edit Partner Information",
    subtitle: "Basic information associated with your partner account.",
    source: "partner",
    fields: PARTNER_INFO_FIELDS,
  },
  partnerAddress: {
    title: "Edit Partner Address",
    subtitle: "Registered address information.",
    source: "partner",
    fields: PARTNER_ADDRESS_FIELDS,
  },
  companyDetails: {
    title: "Edit Company Details",
    subtitle: "Legal and business information of your company.",
    source: "company",
    fields: COMPANY_FIELDS,
    textareas: [
      { key: "companyDescription", label: "Company Description" },
      { key: "aboutCompany", label: "About Company" },
    ],
  },
  companyAddress: {
    title: "Edit Company Address",
    subtitle: "Official company address and location details.",
    source: "company",
    fields: COMPANY_ADDRESS_FIELDS,
    textareas: COMPANY_ADDRESS_TEXTAREAS,
  },
  companySocial: {
    title: "Edit Social Links",
    subtitle: "Social media profiles for your company.",
    source: "company",
    fields: COMPANY_SOCIAL_FIELDS,
  },
  additional: {
    title: "Edit Additional Information",
    subtitle: "Additional remarks related to your company profile.",
    source: "company",
    fields: [],
    textareas: [{ key: "remark", label: "Remark" }],
  },
};

/* ---------------------------------------------------------------
   Helpers
---------------------------------------------------------------- */

const isFilled = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const getLoggedUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error("Invalid user in localStorage:", error);
    return null;
  }
};

// Reads the body as JSON when possible, otherwise as plain text.
const parseResponse = async (response) => {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  return data.message || data.error || data.details || data.msg || fallback;
};

const sendJson = (url, method, body) =>
  fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });

// Accepts a plain array, or an array inside { data } / { content }.
const extractList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

const normalizePartner = (data) => ({ ...emptyPartner, ...(data || {}) });

// Handles both { id, companyLegalName } and { companyDetails: { id, companyLegalName } }
const normalizeCompany = (data, partnerId = "") => {
  let source = data && typeof data === "object" ? data : {};

  if (
    source.companyDetails &&
    typeof source.companyDetails === "object" &&
    !source.companyLegalName &&
    !source.companyDisplayName
  ) {
    source = source.companyDetails;
  }

  return {
    ...emptyCompany,
    ...source,
    partnerId: source.partnerId || partnerId || "",
  };
};

const buildPartnerPayload = (partner) => ({
  ...Object.fromEntries(
    PARTNER_PAYLOAD_KEYS.map((key) => [key, partner[key] || ""])
  ),
  status: partner.status || null,
});

const buildCompanyPayload = (company, partnerId) => ({
  partnerId,
  ...Object.fromEntries(
    COMPANY_PAYLOAD_KEYS.map((key) => [key, company[key] || ""])
  ),
  status: company.status || null,
});

// A company record is only created once the user has actually typed something.
const hasCompanyData = (company) => {
  const ignored = ["id", "partnerId", "status", ...DOCUMENT_FILE_KEYS];

  return Object.entries(company || {}).some(
    ([key, value]) => !ignored.includes(key) && isFilled(value)
  );
};

const validateDocument = (type, file) => {
  if (!file) {
    throw new Error("Please select a document.");
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error("File size must be 10 MB or less.");
  }

  if (type === "logo" && !file.type.startsWith("image/")) {
    throw new Error("Company logo must be an image.");
  }

  if (type !== "logo" && !ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
    throw new Error("Only PDF, JPG, JPEG, PNG or WEBP files are allowed.");
  }
};

/* ---------------------------------------------------------------
   Styles
---------------------------------------------------------------- */

const styles = `
.partner-profile-page {
  /* ---- Colours ---- */
  --primary: #3E0F8D;
  --primary-soft: #5D28A8;
  --secondary: #9564DD;
  --accent: #E4DA72;
  --bg: #EEEEEE;
  --text: #252525;
  --muted: #6B7280;
  --border: #E5E7EB;
  --success: #15803D;
  --success-bg: #DCFCE7;
  --danger: #B91C1C;
  --danger-bg: #FEE2E2;
  --warning: #A16207;
  --warning-bg: #FEF3C7;

  /* ---- Font scale (change sizes here only) ---- */
  --fs-2xs: 10.5px;
  --fs-xs: 11px;
  --fs-sm: 12px;
  --fs-base: 13px;
  --fs-md: 14px;
  --fs-lg: 16px;
  --fs-xl: 18px;
  --fs-hero: 26px;

  /* ---- Radius, spacing, motion ---- */
  --r-sm: 10px;
  --r-md: 12px;
  --r-lg: 18px;
  --gap: 18px;
  --ease: cubic-bezier(0.4, 0, 0.2, 1);
  --t-fast: 0.18s var(--ease);
  --t-base: 0.28s var(--ease);
  --shadow-sm: 0 4px 16px rgba(42, 16, 69, 0.05);
  --shadow-md: 0 12px 30px rgba(42, 16, 69, 0.1);

  max-width: 1180px;
  margin: 0 auto;
  min-height: 100vh;
  padding: 18px;
  background: #f6f7fb;
  color: var(--text);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: var(--fs-base);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  animation: profileFadeIn 0.45s var(--ease);
}

.partner-profile-page *,
.partner-profile-page *::before,
.partner-profile-page *::after {
  box-sizing: border-box;
}

.partner-profile-page input,
.partner-profile-page textarea,
.partner-profile-page button {
  font-family: inherit;
}

@keyframes profileFadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes profileSlideIn {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes profileSlideUp {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes modalPopIn {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

/* ---------- Hero ---------- */

.profile-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: var(--gap);
  padding: 18px 20px;
  border-radius: 14px;
  color: #fff;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-soft) 55%, var(--secondary) 100%);
  box-shadow: 0 16px 40px rgba(62, 15, 141, 0.2);
}

.hero-left {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}

.company-avatar {
  width: 72px;
  height: 72px;
  min-width: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.15);
  font-size: 28px;
  font-weight: 800;
  transition: background var(--t-base), color var(--t-base);
}

.company-avatar.has-logo {
  background: var(--accent);
  border-color: transparent;
  color: var(--primary);
}

.hero-content {
  min-width: 0;
}

.hero-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.hero-title-row h1 {
  margin: 0;
  color: #fff;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.4px;
}

.hero-designation {
  margin: 4px 0 12px;
  font-size: var(--fs-md);
  opacity: 0.88;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  font-size: var(--fs-sm);
}

.hero-meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  opacity: 0.92;
}

.hero-actions {
  display: flex;
  flex-shrink: 0;
  gap: 10px;
}

.status-badge {
  padding: 4px 11px;
  border-radius: 999px;
  background: var(--accent);
  color: var(--primary);
  font-size: var(--fs-2xs);
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.status-badge.active,
.status-badge.approved {
  background: var(--success-bg);
  color: var(--success);
}

.status-badge.pending {
  background: var(--warning-bg);
  color: var(--warning);
}

.status-badge.inactive,
.status-badge.rejected,
.status-badge.blocked {
  background: var(--danger-bg);
  color: var(--danger);
}

/* ---------- Buttons ---------- */

.btn {
  padding: 10px 18px;
  border: 1px solid transparent;
  border-radius: var(--r-md);
  font-size: var(--fs-sm);
  font-weight: 700;
  cursor: pointer;
  transition: transform var(--t-fast), box-shadow var(--t-fast), background var(--t-fast), opacity var(--t-fast);
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn:active:not(:disabled) {
  transform: translateY(0);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn:focus-visible,
.upload-button:focus-within {
  outline: 3px solid rgba(149, 100, 221, 0.45);
  outline-offset: 2px;
}

.btn-light {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.35);
  color: #fff;
}

.btn-light:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.26);
}

.btn-accent {
  background: var(--accent);
  color: var(--primary);
}

.btn-accent:hover:not(:disabled) {
  box-shadow: 0 8px 20px rgba(228, 218, 114, 0.4);
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  box-shadow: 0 8px 20px rgba(62, 15, 141, 0.25);
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-soft);
}

.btn-outline {
  background: #fff;
  border-color: var(--border);
  color: var(--primary);
}

.btn-outline:hover:not(:disabled) {
  border-color: var(--secondary);
}

/* ---------- Alerts ---------- */

.alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 10px 0;
  padding: 10px 12px;
  border-radius: var(--r-md);
  font-size: var(--fs-sm);
  animation: profileSlideIn 0.3s var(--ease);
}

.alert strong {
  display: block;
  margin-bottom: 1px;
  font-size: var(--fs-base);
}

.alert p {
  margin: 0;
}

.alert-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  font-size: var(--fs-sm);
  font-weight: 800;
}

.alert-success { background: var(--success-bg); color: var(--success); }
.alert-success .alert-icon { background: var(--success); }
.alert-error { background: var(--danger-bg); color: var(--danger); }
.alert-error .alert-icon { background: var(--danger); }

/* ---------- Quick stats ---------- */

.quick-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 12px 0;
}

.quick-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--r-lg);
  transition: transform var(--t-base), box-shadow var(--t-base);
}

.quick-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.quick-card-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-md);
  font-size: var(--fs-sm);
  font-weight: 800;
}

.quick-card-icon.purple { background: rgba(62, 15, 141, 0.1); color: var(--primary); }
.quick-card-icon.green  { background: var(--success-bg); color: var(--success); }
.quick-card-icon.yellow { background: rgba(228, 218, 114, 0.4); color: var(--warning); }

.quick-card span {
  display: block;
  color: var(--muted);
  font-size: var(--fs-xs);
}

.quick-card strong {
  color: var(--primary);
  font-size: var(--fs-xl);
  font-weight: 800;
  line-height: 1.3;
}

/* ---------- Layout ---------- */

.profile-layout {
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr);
  align-items: start;
  gap: 14px;
}

.profile-sidebar {
  position: sticky;
  top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sidebar-card,
.profile-section {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-sm);
}

.sidebar-card {
  padding: 14px;
}

.sidebar-profile {
  text-align: center;
}

.sidebar-avatar {
  width: 64px;
  height: 64px;
  margin: 0 auto 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: #fff;
  font-size: 24px;
  font-weight: 800;
}

.sidebar-profile h3 {
  margin: 0 0 2px;
  color: var(--primary);
  font-size: var(--fs-lg);
  font-weight: 800;
}

.sidebar-profile p {
  margin: 0;
  color: var(--muted);
  font-size: var(--fs-sm);
}

.sidebar-divider {
  height: 1px;
  margin: 16px 0;
  background: var(--border);
}

.sidebar-item + .sidebar-item {
  margin-top: 12px;
}

.sidebar-item span {
  display: block;
  margin-bottom: 1px;
  color: var(--muted);
  font-size: var(--fs-xs);
}

.sidebar-item strong {
  font-size: var(--fs-base);
  font-weight: 600;
  word-break: break-word;
}

.strength-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.strength-header h3 {
  margin: 0;
  font-size: var(--fs-md);
}

.strength-header strong {
  color: var(--primary);
  font-size: var(--fs-md);
}

.progress-track {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: #ECE7F6;
}

.progress-value {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--primary), var(--secondary));
  transition: width 0.6s var(--ease);
}

.strength-text {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: var(--fs-xs);
  line-height: 1.6;
}

.info-card {
  display: flex;
  gap: 10px;
  background: rgba(228, 218, 114, 0.18);
}

.info-card-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--success-bg);
  color: var(--success);
  font-size: var(--fs-sm);
  font-weight: 800;
}

.info-card h4 {
  margin: 0 0 2px;
  font-size: var(--fs-base);
}

.info-card p {
  margin: 0;
  color: var(--muted);
  font-size: var(--fs-xs);
  line-height: 1.6;
}

/* ---------- Main content ---------- */

.profile-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-section {
  padding: 16px;
  border-radius: 12px;
}

.section-head-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.section-head-row .section-title {
  margin: 0;
}

.section-title {
  margin-bottom: 16px;
}

.section-eyebrow {
  margin-bottom: 2px;
  color: var(--secondary);
  font-size: var(--fs-xs);
  font-weight: 800;
}

.section-title h2 {
  margin: 0 0 2px;
  color: var(--primary);
  font-size: var(--fs-lg);
  font-weight: 800;
}

.section-title p {
  margin: 0;
  color: var(--muted);
  font-size: var(--fs-sm);
}

.section-btn {
  border: 0;
  border-radius: 7px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity var(--t-fast), transform var(--t-fast);
}

.section-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.section-btn.edit { background: var(--primary); color: #fff; }
.section-btn:disabled { opacity: 0.55; cursor: not-allowed; }

/* ---------- Form ---------- */

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field-full {
  grid-column: 1 / -1;
}

.field label {
  color: #3d3349;
  font-size: 11px;
  font-weight: 700;
}

.field input,
.field textarea {
  width: 100%;
  min-height: 38px;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: #fff;
  color: var(--text);
  font-size: 13px;
  outline: none;
  transition: border-color var(--t-fast), box-shadow var(--t-fast), background var(--t-base), color var(--t-base);
}

.field input:hover:not(:disabled),
.field textarea:hover:not(:disabled) {
  border-color: #CFC6E0;
}

.field input:focus,
.field textarea:focus {
  border-color: var(--secondary);
  box-shadow: 0 0 0 3px rgba(149, 100, 221, 0.15);
}

.field input:disabled,
.field textarea:disabled {
  background: #F5F5F7;
  color: var(--muted);
  cursor: not-allowed;
}

.field textarea {
  min-height: 96px;
  line-height: 1.6;
  resize: vertical;
}

.textarea-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 10px;
  margin-top: 16px;
}

.textarea-grid .field-full {
  grid-column: auto;
}

/* ---------- Documents ---------- */

.document-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.document-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #FBFAFE;
  border: 1px solid var(--border);
  border-radius: var(--r-lg);
  transition: transform var(--t-base), box-shadow var(--t-base), border-color var(--t-base);
}

.document-card:hover {
  transform: translateY(-2px);
  border-color: #D9CCF0;
  box-shadow: var(--shadow-md);
}

.document-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.document-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-md);
  background: var(--primary);
  color: #fff;
  font-size: var(--fs-2xs);
  font-weight: 800;
}

.document-card .status {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: var(--fs-2xs);
  font-weight: 700;
}

.document-card .status.success { background: var(--success-bg); color: var(--success); }
.document-card .status.pending { background: var(--warning-bg); color: var(--warning); }

.document-card h3 {
  margin: 2px 0 0;
  color: var(--primary);
  font-size: 14px;
  font-weight: 800;
}

.document-card p {
  margin: 5px 0 8px;
  color: var(--muted);
  font-size: 11px;
  line-height: 1.5;
}

.uploaded-file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px;
  border-radius: var(--r-sm);
  background: var(--success-bg);
}

.file-check {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--success);
  color: #fff;
  font-size: var(--fs-xs);
}

.file-info {
  min-width: 0;
}

.file-info strong {
  display: block;
  overflow: hidden;
  color: var(--success);
  font-size: var(--fs-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-info span {
  color: var(--muted);
  font-size: var(--fs-2xs);
}

.no-file {
  padding: 10px;
  border: 1px dashed #D1D5DB;
  border-radius: var(--r-sm);
  color: var(--muted);
  font-size: var(--fs-sm);
  text-align: center;
}

.upload-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: var(--r-md);
  background: var(--primary);
  color: #fff;
  font-size: var(--fs-sm);
  font-weight: 700;
  cursor: pointer;
  transition: background var(--t-fast), opacity var(--t-fast), transform var(--t-fast);
}

.upload-button:hover {
  background: var(--primary-soft);
}

.upload-button:active {
  transform: scale(0.99);
}

.upload-button.uploading {
  opacity: 0.7;
  cursor: progress;
}

.upload-button input {
  display: none;
}

.document-card small {
  color: var(--muted);
  font-size: var(--fs-2xs);
  text-align: center;
}

.document-note {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: var(--r-md);
  background: rgba(228, 218, 114, 0.25);
  font-size: var(--fs-sm);
}

.document-note span {
  color: var(--muted);
}

/* ---------- Edit modal ---------- */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(20, 12, 34, 0.5);
  backdrop-filter: blur(2px);
  animation: modalFadeIn 0.18s var(--ease);
}

.modal-card {
  width: 100%;
  max-width: 640px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: var(--r-lg);
  box-shadow: 0 24px 60px rgba(20, 12, 34, 0.35);
  animation: modalPopIn 0.22s var(--ease);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border);
}

.modal-header h2 {
  margin: 0 0 2px;
  color: var(--primary);
  font-size: var(--fs-lg);
  font-weight: 800;
}

.modal-header p {
  margin: 0;
  color: var(--muted);
  font-size: var(--fs-sm);
}

.modal-close {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: #F1EEFA;
  color: var(--primary);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background var(--t-fast);
}

.modal-close:hover {
  background: #E4DDF5;
}

.modal-body {
  padding: 20px 22px;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid var(--border);
  background: #FAFAFC;
}

/* ---------- Save bar ---------- */

.save-bar {
  position: sticky;
  bottom: 16px;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--r-lg);
  box-shadow: 0 14px 36px rgba(42, 16, 69, 0.18);
  animation: profileSlideUp 0.3s var(--ease);
}

.save-bar strong {
  display: block;
  font-size: var(--fs-base);
}

.save-bar span {
  color: var(--muted);
  font-size: var(--fs-sm);
}

.save-actions {
  display: flex;
  gap: 10px;
}

/* ---------- Loading ---------- */

.profile-loading {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #EEEEEE;
  color: #3E0F8D;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  -webkit-font-smoothing: antialiased;
}

.profile-loading h3 { margin: 12px 0 0; font-size: 16px; }
.profile-loading p  { margin: 0; color: #6B7280; font-size: 13px; }

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(62, 15, 141, 0.15);
  border-top-color: #3E0F8D;
  border-radius: 50%;
  animation: profileSpin 0.8s linear infinite;
}

@keyframes profileSpin {
  to { transform: rotate(360deg); }
}

/* ---------- Responsive ---------- */

@media (max-width: 900px) {
  .profile-layout {
    grid-template-columns: 1fr;
  }

  .profile-sidebar {
    display: none;
  }

  .document-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .partner-profile-page {
    padding: 14px;
  }

  .profile-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-actions {
    width: 100%;
  }

  .hero-actions .btn {
    flex: 1;
  }

  .quick-stats {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .hero-left {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-title-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  /* 16px stops iPhones from zooming into the field */
  .field input,
  .field textarea {
    font-size: 16px;
  }

  .save-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .save-actions .btn {
    flex: 1;
  }

  .modal-card {
    max-height: 92vh;
  }
}

@media (prefers-reduced-motion: reduce) {
  .partner-profile-page,
  .partner-profile-page *,
  .partner-profile-page *::before,
  .partner-profile-page *::after {
    animation: none !important;
    transition: none !important;
  }
}
`;

/* ---------------------------------------------------------------
   Small components
---------------------------------------------------------------- */

function SectionTitle({ eyebrow, title, subtitle }) {
  return (
    <div className="section-title">
      {eyebrow && <div className="section-eyebrow">{eyebrow}</div>}
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  disabled = false,
  type = "text",
  fullWidth = false,
}) {
  const id = useId();

  return (
    <div className={`field${fullWidth ? " field-full" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, disabled = false }) {
  const id = useId();

  return (
    <div className="field field-full">
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </div>
  );
}

function DocumentCard({
  type,
  fileName,
  uploading,
  disabled,
  onUpload,
  onView,
}) {
  const config = DOCUMENTS[type];
  const uploaded = Boolean(fileName);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      onUpload(type, file);
    }

    // Lets the user pick the same file again later.
    e.target.value = "";
  };

  return (
    <div className="document-card">
      <div className="document-top">
        <div className="document-icon">{config.short}</div>

        {uploaded ? (
          <span className="status success">✓ Uploaded</span>
        ) : (
          <span className="status pending">Pending</span>
        )}
      </div>

      <h3>{config.title}</h3>
      <p>{config.description}</p>

      {uploaded ? (
        <div className="uploaded-file">
          <div className="file-check">✓</div>
          <div className="file-info">
            <strong>{fileName}</strong>
            <span>Saved in database</span>
          </div>
        </div>
      ) : (
        <div className="no-file">No document uploaded</div>
      )}

      <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
        {uploaded && (
          <button
            type="button"
            className="btn btn-outline"
            style={{ flex: 1 }}
            onClick={() => onView?.(type)}
            disabled={uploading}
          >
            View
          </button>
        )}

        <label
          className={`upload-button${uploading ? " uploading" : ""}`}
          style={{ flex: 1 }}
        >
          {uploading ? "Uploading..." : uploaded ? "Replace" : "Upload"}

          <input
            type="file"
            accept={config.accept}
            disabled={uploading || disabled}
            onChange={handleFileChange}
          />
        </label>
      </div>

      <small>Maximum file size: 10 MB</small>
    </div>
  );
}

// The single edit modal used by every section on the page.
function EditModal({ config, draft, onChange, onCancel, onSave, saving }) {
  if (!config) return null;

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onCancel}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{config.title}</h2>
            {config.subtitle && <p>{config.subtitle}</p>}
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onCancel}
            disabled={saving}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          {config.fields.length > 0 && (
            <div className="form-grid">
              {config.fields.map(({ key, label, full, locked }) => (
                <Field
                  key={key}
                  label={label}
                  value={draft[key]}
                  disabled={locked}
                  fullWidth={full}
                  onChange={(value) => onChange(key, value)}
                />
              ))}
            </div>
          )}

          {config.textareas?.length > 0 && (
            <div className="textarea-grid">
              {config.textareas.map(({ key, label }) => (
                <TextAreaField
                  key={key}
                  label={label}
                  value={draft[key]}
                  onChange={(value) => onChange(key, value)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-outline"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   Page
---------------------------------------------------------------- */

export default function PartnerProfile() {
  const [partner, setPartner] = useState(emptyPartner);
  const [company, setCompany] = useState(emptyCompany);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState("");

  // Which section's modal is open (null = none) and its draft values.
  const [modalSection, setModalSection] = useState(null);
  const [modalDraft, setModalDraft] = useState({});

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loggedUser = useMemo(() => getLoggedUser(), []);

  const applyPartner = useCallback((value) => setPartner(value), []);
  const applyCompany = useCallback((value) => setCompany(value), []);

  /* ---------------- Load data ---------------- */

  const fetchCompanyDetails = useCallback(
    async (partnerId) => {
      if (!partnerId) {
        applyCompany({ ...emptyCompany });
        return null;
      }

      try {
        const response = await fetch(
          `${API_BASE}/company-details/partner/${partnerId}`,
          { headers: { Accept: "application/json" } }
        );

        // No company record yet: show an empty form.
        if (response.status === 404) {
          const blank = blankCompany(partnerId);
          applyCompany(blank);
          return null;
        }

        const data = await parseResponse(response);

        if (!response.ok) {
          throw new Error(
            getErrorMessage(data, "Unable to load company details.")
          );
        }

        const normalized = normalizeCompany(data, partnerId);
        applyCompany(normalized);
        return normalized;
      } catch (err) {
        console.error("Company details error:", err);
        applyCompany(blankCompany(partnerId));
        return null;
      }
    },
    [applyCompany]
  );

  const fetchPartner = useCallback(
    async (showLoader = true) => {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError("");

      try {
        const response = await fetch(`${API_BASE}/partners/all`, {
          headers: { Accept: "application/json" },
        });

        const data = await parseResponse(response);

        if (!response.ok) {
          throw new Error(getErrorMessage(data, "Unable to load partners."));
        }

        const email = loggedUser?.email?.trim()?.toLowerCase();

        const found = extractList(data).find(
          (item) => item?.email?.trim()?.toLowerCase() === email
        );

        if (!found) {
          throw new Error(
            `Partner profile not found for ${
              loggedUser?.email || "logged-in user"
            }.`
          );
        }

        const normalized = normalizePartner(found);
        applyPartner(normalized);

        await fetchCompanyDetails(normalized.id);
      } catch (err) {
        console.error("Partner profile error:", err);
        setError(err.message || "Unable to load profile.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [applyPartner, fetchCompanyDetails, loggedUser]
  );

  useEffect(() => {
    fetchPartner(true);
  }, [fetchPartner]);

  /* ---------------- Save (used by the modal) ---------------- */

  const savePartnerData = async (data) => {
    if (!data.id) {
      throw new Error("Partner ID is missing.");
    }

    const response = await sendJson(
      `${API_BASE}/partners/${data.id}`,
      "PUT",
      buildPartnerPayload(data)
    );

    const resData = await parseResponse(response);

    if (!response.ok) {
      throw new Error(
        getErrorMessage(resData, "Partner details could not be saved.")
      );
    }

    const returned = resData && typeof resData === "object" ? resData : {};
    const merged = normalizePartner({ ...data, ...returned });
    applyPartner(merged);
    return merged;
  };

  const saveCompanyData = async (data) => {
    if (!partner.id) {
      throw new Error("Partner ID is missing.");
    }

    const isUpdate = Boolean(data.id);

    const response = await sendJson(
      isUpdate
        ? `${API_BASE}/company-details/${data.id}`
        : `${API_BASE}/company-details`,
      isUpdate ? "PUT" : "POST",
      buildCompanyPayload(data, partner.id)
    );

    const resData = await parseResponse(response);

    if (!response.ok) {
      throw new Error(
        getErrorMessage(resData, "Company details could not be saved.")
      );
    }

    let saved = normalizeCompany(resData, partner.id);

    // The backend may not return the record, so load it again.
    if (!saved.id) {
      const fresh = await fetchCompanyDetails(partner.id);
      if (fresh) saved = fresh;
    }

    applyCompany(saved);
    return saved;
  };

  /* ---------------- Modal handling ---------------- */

  const openModal = (section) => {
    const config = MODAL_CONFIG[section];
    if (!config) return;

    const source = config.source === "partner" ? partner : company;
    setModalDraft({ ...source });
    setModalSection(section);
    setError("");
    setSuccess("");
  };

  const closeModal = () => {
    if (saving) return;
    setModalSection(null);
    setModalDraft({});
  };

  const updateModalField = (key, value) =>
    setModalDraft((prev) => ({ ...prev, [key]: value }));

  const handleModalSave = async () => {
    const config = MODAL_CONFIG[modalSection];
    if (!config) return;

    setError("");
    setSaving(true);

    try {
      if (config.source === "partner") {
        await savePartnerData({ ...partner, ...modalDraft });
      } else {
        const draftCompany = { ...company, ...modalDraft };
        if (!company.id && !hasCompanyData(draftCompany)) {
          throw new Error(
            "Please enter at least one company detail before saving."
          );
        }
        await saveCompanyData(draftCompany);
      }

      setModalSection(null);
      setModalDraft({});
      setSuccess("Changes saved successfully.");

      await Swal.fire({
        icon: "success",
        title: "Saved!",
        text: "Your changes have been updated successfully.",
        confirmButtonColor: "#3E0F8D",
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (err) {
      console.error("Save error:", err);
      const message = err.message || "Unable to save changes.";
      setError(message);

      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: message,
        confirmButtonColor: "#3E0F8D",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setSuccess("");
    setError("");
    await fetchPartner(false);
  };

  /* ---------------- Documents ---------------- */

  // Upload endpoints in CompanyDetailsController use PARTNER ID,
  // not CompanyDetails ID. The backend expects @RequestParam("file").
  const uploadDocument = async (type, file) => {
    setError("");
    setSuccess("");

    try {
      validateDocument(type, file);

      if (!partner.id) {
        throw new Error("Partner ID is missing. Cannot upload document.");
      }

      setUploadingType(type);

      const config = DOCUMENTS[type];
      if (!config) {
        throw new Error("Invalid document type.");
      }

      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(config.endpoint(partner.id), {
        method: "POST",
        body: formData,
      });

      const data = await parseResponse(response);

      if (!response.ok) {
        throw new Error(getErrorMessage(data, `${config.title} upload failed.`));
      }

      // Controller returns CompanyDetailsResponse after upload.
      if (data && typeof data === "object" && !Array.isArray(data)) {
        const updated = normalizeCompany(data, partner.id);
        applyCompany(updated);
      } else {
        const fresh = await fetchCompanyDetails(partner.id);
        if (!fresh) {
          throw new Error(
            `${config.title} uploaded, but company details could not be refreshed.`
          );
        }
      }

      setSuccess(`${config.title} uploaded successfully.`);

      await Swal.fire({
        icon: "success",
        title: "Uploaded!",
        text: `${config.title} was uploaded successfully.`,
        confirmButtonColor: "#3E0F8D",
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (err) {
      console.error(`${type} upload error:`, err);
      const message =
        err.message || `${DOCUMENTS[type]?.title || "Document"} upload failed.`;
      setError(message);

      Swal.fire({
        icon: "error",
        title: "Upload failed",
        text: message,
        confirmButtonColor: "#3E0F8D",
      });
    } finally {
      setUploadingType("");
    }
  };

  // Opens the GET endpoint provided by CompanyDetailsController.
  const openDocument = (type) => {
    if (!partner.id) {
      setError("Partner ID is missing.");
      return;
    }

    const config = DOCUMENTS[type];
    if (!config?.viewEndpoint) {
      setError("Document view endpoint is not configured.");
      return;
    }

    window.open(config.viewEndpoint(partner.id), "_blank", "noopener,noreferrer");
  };

  /* ---------------- Derived values ---------------- */

  const uploadedCount = DOCUMENT_FILE_KEYS.filter((key) =>
    isFilled(company[key])
  ).length;

  const completion = useMemo(() => {
    const allFields = [
      partner.name,
      partner.email,
      partner.mobile,
      partner.companyName,
      partner.city,
      partner.state,
      partner.address,
      company.companyLegalName,
      company.industry,
      company.officialEmail,
      company.officialPhone,
      company.registeredAddress,
      company.city,
      company.state,
      company.gstNumber,
      company.panNumber,
      ...DOCUMENT_FILE_KEYS.map((key) => company[key]),
    ];

    const filled = allFields.filter(isFilled).length;
    return Math.round((filled / allFields.length) * 100);
  }, [partner, company]);

  const companyTitle =
    company.companyDisplayName ||
    company.companyLegalName ||
    partner.companyName ||
    partner.name;

  const initial = (companyTitle || "C").charAt(0).toUpperCase();
  const designation = partner.designation || "Business Partner";
  const status = partner.status || company.status || "ACTIVE";
  const location = [partner.city, partner.state].filter(Boolean).join(", ");

  const renderReadOnlyFields = (fields, data) =>
    fields.map(({ key, label, full }) => (
      <Field key={key} label={label} value={data[key]} disabled fullWidth={full} />
    ));

  /* ---------------- Render ---------------- */

  if (loading) {
    return (
      <>
        <style>{styles}</style>

        <div className="profile-loading">
          <div className="loading-spinner" />
          <h3>Loading Partner Profile...</h3>
          <p>Please wait...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <PartnerHeader />

      <div className="partner-profile-page">
        {/* ================= HERO ================= */}
        <section className="profile-hero">
          <div className="hero-left">
            <div className={`company-avatar${company.logoFileName ? " has-logo" : ""}`}>
              {company.logoFileName ? (
                <img
                  src={`${API_BASE}/company-details/partner/${partner.id}/logo`}
                  alt={`${companyTitle || "Company"} logo`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                initial
              )}
            </div>

            <div className="hero-content">
              <div className="hero-title-row">
                <h1>{companyTitle || "Partner Profile"}</h1>

                <span className={`status-badge ${String(status).toLowerCase()}`}>
                  {status}
                </span>
              </div>

              <p className="hero-designation">{designation}</p>

              <div className="hero-meta">
                {partner.email && (
                  <span>
                    <strong>✉</strong>
                    {partner.email}
                  </span>
                )}

                {location && (
                  <span>
                    <strong>⌖</strong>
                    {location}
                  </span>
                )}

                {partner.mobile && (
                  <span>
                    <strong>☎</strong>
                    {partner.mobile}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="hero-actions">
            <button
              type="button"
              className="btn btn-light"
              onClick={handleRefresh}
              disabled={refreshing || saving}
            >
              {refreshing ? "Refreshing..." : "↻ Refresh"}
            </button>
          </div>
        </section>

        {/* ================= ALERTS ================= */}
        {success && (
          <div className="alert alert-success" role="status">
            <span className="alert-icon">✓</span>
            <div>
              <strong>Success</strong>
              <p>{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-error" role="alert">
            <span className="alert-icon">!</span>
            <div>
              <strong>Something went wrong</strong>
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* ================= QUICK STATS ================= */}
        <section className="quick-stats">
          <div className="quick-card">
            <div className="quick-card-icon purple">#</div>
            <div>
              <span>Partner ID</span>
              <strong>{partner.id || "—"}</strong>
            </div>
          </div>

          <div className="quick-card">
            <div className="quick-card-icon green">%</div>
            <div>
              <span>Profile Completion</span>
              <strong>{completion}%</strong>
            </div>
          </div>

          <div className="quick-card">
            <div className="quick-card-icon yellow">DOC</div>
            <div>
              <span>Documents</span>
              <strong>
                {uploadedCount} / {DOCUMENT_FILE_KEYS.length}
              </strong>
            </div>
          </div>
        </section>

        <div className="profile-layout">
          {/* ================= SIDEBAR ================= */}
          <aside className="profile-sidebar">
            <div className="sidebar-card">
              <div className="sidebar-profile">
                <div className="sidebar-avatar">{initial}</div>
                <h3>{companyTitle || "Partner"}</h3>
                <p>{designation}</p>
              </div>

              <div className="sidebar-divider" />

              <div className="sidebar-item">
                <span>Email</span>
                <strong>{partner.email || "Not added"}</strong>
              </div>

              <div className="sidebar-item">
                <span>Mobile</span>
                <strong>{partner.mobile || "Not added"}</strong>
              </div>

              <div className="sidebar-item">
                <span>Company</span>
                <strong>{companyTitle || "Not added"}</strong>
              </div>

              <div className="sidebar-item">
                <span>Location</span>
                <strong>{location || "Not added"}</strong>
              </div>
            </div>

            <div className="sidebar-card">
              <div className="strength-header">
                <h3>Profile Strength</h3>
                <strong>{completion}%</strong>
              </div>

              <div className="progress-track">
                <div className="progress-value" style={{ width: `${completion}%` }} />
              </div>

              <p className="strength-text">
                Complete your profile and company documents to improve your
                profile visibility.
              </p>
            </div>

            <div className="sidebar-card info-card">
              <div className="info-card-icon">✓</div>
              <div>
                <h4>Document Auto Save</h4>
                <p>
                  Selected documents are uploaded directly to the server and
                  stored in your company profile.
                </p>
              </div>
            </div>
          </aside>

          {/* ================= MAIN ================= */}
          <main className="profile-main">
            <section className="profile-section">
              <div className="section-head-row">
                <SectionTitle
                  title="Partner Information"
                  subtitle="Basic information associated with your partner account."
                />
                <button type="button" className="section-btn edit" onClick={() => openModal("partnerInfo")}>
                  ✎ Edit
                </button>
              </div>
              <div className="form-grid">{renderReadOnlyFields(PARTNER_INFO_FIELDS, partner)}</div>
            </section>

            <section className="profile-section">
              <div className="section-head-row">
                <SectionTitle title="Partner Address" subtitle="Registered address information." />
                <button type="button" className="section-btn edit" onClick={() => openModal("partnerAddress")}>
                  ✎ Edit
                </button>
              </div>
              <div className="form-grid">{renderReadOnlyFields(PARTNER_ADDRESS_FIELDS, partner)}</div>
            </section>

            <section className="profile-section">
              <div className="section-head-row">
                <SectionTitle
                  title="Company Details"
                  subtitle="Legal and business information of your company."
                />
                <button type="button" className="section-btn edit" onClick={() => openModal("companyDetails")}>
                  ✎ Edit
                </button>
              </div>
              <div className="form-grid">{renderReadOnlyFields(COMPANY_FIELDS, company)}</div>

              <div className="textarea-grid">
                <TextAreaField label="Company Description" value={company.companyDescription} disabled />
                <TextAreaField label="About Company" value={company.aboutCompany} disabled />
              </div>
            </section>

            {/* <section className="profile-section"> */}
              {/* <div className="section-head-row"> */}
                {/* <SectionTitle */}
                  {/* title="Company Address" */}
                  {/* subtitle="Official company address and location details." */}
                {/* /> */}
                {/* <button type="button" className="section-btn edit" onClick={() => openModal("companyAddress")}> */}
                  {/* ✎ Edit */}
                {/* </button> */}
              {/* </div> */}
              {/* <div className="form-grid">{renderReadOnlyFields(COMPANY_ADDRESS_FIELDS, company)}</div> */}
              {/* <div className="textarea-grid"> */}
                {/* {COMPANY_ADDRESS_TEXTAREAS.map(({ key, label }) => ( */}
                  {/* // <TextAreaField key={key} label={label} value={company[key]} disabled /> */}
                {/* // ))} */}
              {/* </div> 
            </section>  */}

            <section className="profile-section">
              <div className="section-head-row">
                <SectionTitle
                  title="Social Links"
                  subtitle="Social media profiles for your company."
                />
                <button type="button" className="section-btn edit" onClick={() => openModal("companySocial")}>
                  ✎ Edit
                </button>
              </div>
              <div className="form-grid">{renderReadOnlyFields(COMPANY_SOCIAL_FIELDS, company)}</div>
            </section>

            <section className="profile-section">
              <SectionTitle
                title="Company Documents"
                subtitle="Upload company documents. Files are saved directly to the database."
              />

              <div className="document-grid">
                {Object.entries(DOCUMENTS).map(([type, config]) => (
                  <DocumentCard
                    key={type}
                    type={type}
                    fileName={company[config.fileKey]}
                    uploading={uploadingType === type}
                    disabled={saving || Boolean(uploadingType)}
                    onUpload={uploadDocument}
                    onView={openDocument}
                  />
                ))}
              </div>

              <div className="document-note">
                <strong>Maximum file size: 10 MB</strong>
                <span>Documents are uploaded immediately when you select a file.</span>
              </div>
            </section>

            <section className="profile-section">
              <div className="section-head-row">
                <SectionTitle
                  title="Additional Information"
                  subtitle="Additional remarks related to your company profile."
                />
                <button type="button" className="section-btn edit" onClick={() => openModal("additional")}>
                  ✎ Edit
                </button>
              </div>

              <TextAreaField label="Remark" value={company.remark || partner.remark} disabled />
            </section>
          </main>
        </div>
      </div>

      <EditModal
        config={MODAL_CONFIG[modalSection]}
        draft={modalDraft}
        onChange={updateModalField}
        onCancel={closeModal}
        onSave={handleModalSave}
        saving={saving}
      />

      <PartnerFooter />
    </>
  );
}